import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

export interface Collection {
  uuid: string;
  name: string;
  slug: string;
  is_key: number;
  /** Set on collections that back a page (`'about'`) — never deleted or made key. */
  system_key: string | null;
  /** Ordered membership. */
  photo_uuids: string[];
}

export interface PanelPhoto {
  uuid: string;
  thumb_url: string;
  title: string;
}

// Drag payload types. A photo dragged from the grid vs. a thumbnail being
// reordered inside the panel vs. a tab being reordered.
export const PHOTO_DRAG = 'application/x-photo-uuid';
const MEMBER_DRAG = 'application/x-collection-photo';
const TAB_DRAG = 'application/x-collection-uuid';

const has = (e: React.DragEvent, type: string) => e.dataTransfer.types.includes(type);

/** Moves or inserts `uuid` so it sits before `before` (or at the end). */
function placed(list: string[], uuid: string, before: string | null): string[] {
  const rest = list.filter((u) => u !== uuid);
  const at = before ? rest.indexOf(before) : -1;
  return at < 0 ? [...rest, uuid] : [...rest.slice(0, at), uuid, ...rest.slice(at)];
}

interface Props {
  collections: Collection[];
  onChange: (collections: Collection[]) => void;
  photos: Map<string, PanelPhoto>;
}

/**
 * Right-hand collections editor on the Photos page: one tab per collection,
 * photos dragged in from the grid, reordered, or removed. Every change is
 * applied optimistically and rolled back if the API refuses it.
 */
export function CollectionsPanel({ collections, onChange, photos }: Props) {
  const [activeUuid, setActiveUuid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);

  const active = collections.find((c) => c.uuid === activeUuid) ?? collections[0];

  const run = async (next: Collection[], call: () => Promise<unknown>) => {
    const prev = collections;
    onChange(next);
    setError(null);
    try {
      await call();
    } catch (err) {
      onChange(prev);
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const setMembers = (col: Collection, photo_uuids: string[]) =>
    run(
      collections.map((c) => (c.uuid === col.uuid ? { ...c, photo_uuids } : c)),
      () => api.put(`/api/admin/collections/${col.uuid}/photos`, { photo_uuids })
    );

  const create = async () => {
    const name = window.prompt('Collection name');
    if (!name?.trim()) return;
    setError(null);
    try {
      const created = await api.post<Collection>('/api/admin/collections', { name: name.trim() });
      onChange([...collections, created]);
      setActiveUuid(created.uuid);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
  };

  const rename = async (col: Collection, name: string) => {
    setRenaming(null);
    name = name.trim();
    if (!name || name === col.name) return;
    setError(null);
    try {
      const r = await api.put<{ name: string; slug: string }>(`/api/admin/collections/${col.uuid}`, { name });
      onChange(collections.map((c) => (c.uuid === col.uuid ? { ...c, ...r } : c)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rename failed');
    }
  };

  const makeKey = (col: Collection) =>
    run(
      collections.map((c) => ({ ...c, is_key: c.uuid === col.uuid ? 1 : 0 })),
      () => api.post(`/api/admin/collections/${col.uuid}/key`)
    );

  const remove = (col: Collection) => {
    if (!window.confirm(`Delete "${col.name}"? Its photos stay in the gallery.`)) return;
    setActiveUuid(null);
    return run(
      collections.filter((c) => c.uuid !== col.uuid),
      () => api.delete(`/api/admin/collections/${col.uuid}`)
    );
  };

  const reorderTabs = (uuid: string, before: string) => {
    const order = placed(collections.map((c) => c.uuid), uuid, before);
    if (order.join() === collections.map((c) => c.uuid).join()) return;
    const byUuid = new Map(collections.map((c) => [c.uuid, c]));
    return run(
      order.map((u) => byUuid.get(u)!),
      () => api.put('/api/admin/collections/order', { uuids: order })
    );
  };

  // Drop onto a thumbnail (before = its uuid) or the empty part of the body (null).
  const dropMember = (e: React.DragEvent, before: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    if (!active) return;
    const uuid = e.dataTransfer.getData(MEMBER_DRAG) || e.dataTransfer.getData(PHOTO_DRAG);
    if (!uuid || uuid === before) return;
    // A grid drop of a photo already in the collection leaves it where it is.
    if (!e.dataTransfer.getData(MEMBER_DRAG) && active.photo_uuids.includes(uuid)) return;
    const next = placed(active.photo_uuids, uuid, before);
    if (next.join() !== active.photo_uuids.join()) setMembers(active, next);
  };

  const dropOnTab = (e: React.DragEvent, col: Collection) => {
    e.preventDefault();
    setDropTarget(null);
    const tab = e.dataTransfer.getData(TAB_DRAG);
    if (tab) return reorderTabs(tab, col.uuid);
    const photo = e.dataTransfer.getData(PHOTO_DRAG) || e.dataTransfer.getData(MEMBER_DRAG);
    if (photo && !col.photo_uuids.includes(photo)) setMembers(col, [...col.photo_uuids, photo]);
  };

  const acceptsPhoto = (e: React.DragEvent) => has(e, PHOTO_DRAG) || has(e, MEMBER_DRAG);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 48px)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          padding: 8,
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        {collections.map((c) => {
          const isActive = c.uuid === active?.uuid;
          const isTarget = dropTarget === `tab:${c.uuid}`;
          return (
            <div
              key={c.uuid}
              draggable={renaming !== c.uuid}
              onDragStart={(e) => {
                e.dataTransfer.setData(TAB_DRAG, c.uuid);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                if (!has(e, TAB_DRAG) && !acceptsPhoto(e)) return;
                e.preventDefault();
                setDropTarget(`tab:${c.uuid}`);
              }}
              onDragLeave={() => setDropTarget((t) => (t === `tab:${c.uuid}` ? null : t))}
              onDrop={(e) => dropOnTab(e, c)}
              onClick={() => setActiveUuid(c.uuid)}
              onDoubleClick={() => setRenaming(c.uuid)}
              title={
                c.is_key
                  ? 'Key collection — shown on the landing page'
                  : c.system_key
                    ? 'Photos available to the About page'
                    : 'Double-click to rename, drag to reorder'
              }
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                fontSize: 13,
                cursor: 'pointer',
                userSelect: 'none',
                background: isActive ? '#1a1a1a' : isTarget ? '#f3f4f6' : 'transparent',
                color: isActive ? '#fff' : '#374151',
                outline: isTarget ? '2px dashed #9ca3af' : 'none',
              }}
            >
              {renaming === c.uuid ? (
                <input
                  autoFocus
                  defaultValue={c.name}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={(e) => rename(c, e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  style={{ fontSize: 13, width: 120, padding: '0 4px', color: '#111' }}
                />
              ) : (
                <>
                  {c.is_key ? '★ ' : ''}
                  {c.name}
                  <span style={{ opacity: 0.6, marginLeft: 6 }}>{c.photo_uuids.length}</span>
                </>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={create}
          title="New collection"
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 13,
            border: '1px dashed #d1d5db',
            background: '#fff',
            color: '#6b7280',
            cursor: 'pointer',
          }}
        >
          +
        </button>
      </div>

      {error && (
        <div style={{ padding: '8px 12px', fontSize: 12, color: '#991b1b', background: '#fef2f2' }}>{error}</div>
      )}

      {active ? (
        <>
          <div
            onDragOver={(e) => {
              if (!acceptsPhoto(e)) return;
              e.preventDefault();
              setDropTarget('body');
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTarget(null);
            }}
            onDrop={(e) => dropMember(e, null)}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 12,
              minHeight: 200,
              background: dropTarget === 'body' ? '#f9fafb' : '#fff',
              outline: dropTarget === 'body' ? '2px dashed #9ca3af' : 'none',
              outlineOffset: -6,
            }}
          >
            {active.photo_uuids.length === 0 ? (
              <div style={{ padding: '48px 12px', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>
                Drag photos here
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 8 }}>
                {active.photo_uuids.map((uuid) => {
                  const p = photos.get(uuid);
                  const isTarget = dropTarget === `photo:${uuid}`;
                  return (
                    <div
                      key={uuid}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(MEMBER_DRAG, uuid);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragOver={(e) => {
                        if (!acceptsPhoto(e)) return;
                        e.preventDefault();
                        e.stopPropagation();
                        setDropTarget(`photo:${uuid}`);
                      }}
                      onDrop={(e) => dropMember(e, uuid)}
                      title={p?.title}
                      style={{
                        position: 'relative',
                        aspectRatio: '1',
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: '#f3f4f6',
                        cursor: 'grab',
                        boxShadow: isTarget ? '-4px 0 0 #1a1a1a' : 'none',
                      }}
                    >
                      {p && (
                        <img
                          src={p.thumb_url}
                          alt={p.title}
                          draggable={false}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setMembers(active, active.photo_uuids.filter((u) => u !== uuid))}
                        title="Remove from collection"
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          width: 20,
                          height: 20,
                          borderRadius: 10,
                          border: 'none',
                          background: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          fontSize: 12,
                          lineHeight: '20px',
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              borderTop: '1px solid #e5e5e5',
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            <span>
              {active.is_key ? (
                '★ Landing page'
              ) : active.system_key === 'about' ? (
                <Link to="/about" style={{ color: 'inherit' }}>
                  Placed on the About page →
                </Link>
              ) : (
                `/collections/${active.slug}`
              )}
            </span>
            <span style={{ display: 'flex', gap: 8 }}>
              {!active.is_key && !active.system_key && (
                <button type="button" onClick={() => makeKey(active)} style={footerButton}>
                  Make key
                </button>
              )}
              {!active.system_key && (
              <button
                type="button"
                onClick={() => remove(active)}
                disabled={!!active.is_key}
                title={active.is_key ? 'Make another collection key first' : undefined}
                style={{
                  ...footerButton,
                  color: active.is_key ? '#d1d5db' : '#991b1b',
                  cursor: active.is_key ? 'not-allowed' : 'pointer',
                }}
              >
                Delete
              </button>
              )}
            </span>
          </div>
        </>
      ) : (
        <div style={{ padding: 24, fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
          No collections — click + to create one.
        </div>
      )}
    </div>
  );
}

const footerButton: React.CSSProperties = {
  padding: '4px 10px',
  border: '1px solid #e5e5e5',
  background: '#fff',
  borderRadius: 6,
  fontSize: 12,
  color: '#374151',
  cursor: 'pointer',
};
